import React, { useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Loader2, Layers, PenLine, SlidersHorizontal, QrCode } from 'lucide-react';

// Flyer-Vorrat: the iAuto writes identical flyers (no QR) in packages of 50 at any time; each week's letters
// get their QR codes printed onto stock flyers and booked off. Series of stock flyers are rendered by the parent.
export function FlyerStockBlock({ stock, openQr, devices, deviceId, onDeviceChange, onChanged, onOpenQr, renderSeries, DevicePicker, extra = null }) {
  const [busy, setBusy] = useState('');
  const [packages, setPackages] = useState(1);
  if (!stock) return null;
  const size = stock.package_size || 50;
  const avail = stock.available || 0;
  const inProgress = stock.in_progress || 0;
  const short = openQr > avail;

  const write = async () => {
    setBusy('write');
    try {
      const r = await api.writeFlyerStock(packages, deviceId);
      toast.success(`${r.data.count} Vorrats-Flyer vorbereitet${r.data.device_name ? ` · ${r.data.device_name}` : ''} — Blanko-Flyer (ohne QR) einlegen und „Jetzt schreiben“`, { duration: 8000 });
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setBusy(''); }
  };
  const adjust = async () => {
    const raw = window.prompt(`Bestand korrigieren — aktuell ${avail} Flyer.\nÄnderung eingeben (z. B. -3 für beschädigte Flyer oder +10 nach Zählung):`, '');
    if (raw == null || !raw.trim()) return;
    const delta = parseInt(raw.replace(/\s/g, ''), 10);
    if (!delta) { toast.error('Bitte eine ganze Zahl ungleich 0 eingeben'); return; }
    const reason = window.prompt('Grund (optional):', '') || '';
    setBusy('adjust');
    try {
      const r = await api.adjustFlyerStock(delta, reason);
      toast.success(`Bestand korrigiert: jetzt ${r.data.available} Flyer im Vorrat`);
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setBusy(''); }
  };

  return (
    <div className={`border rounded-sm p-2.5 space-y-1.5 ${short ? 'border-amber-500/50 bg-amber-500/5' : 'border-sky-500/40 bg-sky-500/5'}`} data-testid="flyer-stock-block">
      <div className="flex items-center gap-1.5 flex-wrap">
        <Layers className="w-3.5 h-3.5 text-sky-300" />
        <span className="text-xs font-medium text-foreground">Flyer-Vorrat</span>
        <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${short ? 'text-amber-300 border-amber-500/40 bg-amber-500/10' : 'text-sky-300 border-sky-500/40 bg-sky-500/10'}`}
          data-testid="flyer-stock-count" title="Fertig geschriebene Flyer ohne QR-Code, die auf ihre Briefe warten">
          {avail} fertig{inProgress ? ` · ${inProgress} in Arbeit` : ''}
        </span>
        <span className="text-[10px] text-muted-foreground font-mono ml-auto" data-testid="flyer-stock-open-qr">
          {openQr} QR-Code{openQr !== 1 ? 's' : ''} offen
        </span>
      </div>
      <p className="text-[10px] text-muted-foreground">
        Die iAuto schreibt den (immer gleichen) Flyer-Text in {size}er-Paketen auf Blanko-Flyer ohne QR — jederzeit, unabhängig von Briefen.
        Die QR-Codes der aktuellen Briefe drucken Sie später auf Vorrats-Flyer und buchen sie ab.
      </p>
      {short && (
        <p className="text-[10px] text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded-sm px-2 py-1" data-testid="flyer-stock-short">
          Vorrat zu klein: {openQr} QR-Codes offen, aber nur {avail} Flyer geschrieben — erst ein Vorrats-Paket schreiben lassen.
        </p>
      )}
      {renderSeries}
      <div className="flex items-center gap-2 flex-wrap">
        <select value={packages} onChange={e => setPackages(parseInt(e.target.value, 10))} data-testid="flyer-stock-packages"
          className="h-6 text-[10px] rounded-sm bg-secondary/40 border border-border px-1 text-foreground focus:outline-none">
          {[1, 2, 3, 4, 5, 6, 8, 10].map(n => <option key={n} value={n}>{n} Paket{n > 1 ? 'e' : ''} = {n * size} Flyer</option>)}
        </select>
        <Button size="sm" variant="outline" onClick={write} disabled={busy === 'write'} data-testid="flyer-stock-write-btn"
          className="h-6 text-[10px] rounded-sm border-sky-500/40 text-sky-300 hover:bg-sky-500/10 disabled:opacity-50">
          {busy === 'write' ? <Loader2 className="w-3 h-3 animate-spin" /> : <PenLine className="w-3 h-3" />}
          {packages * size} Flyer schreiben (Vorrat)
        </Button>
        {DevicePicker && <DevicePicker devices={devices} value={deviceId} onChange={onDeviceChange} testId="flyer-stock-device" />}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onOpenQr} data-testid="flyer-stock-qr-btn"
          className="flex items-center gap-1 text-[10px] text-sky-300 hover:text-sky-200 transition-colors">
          <QrCode className="w-3 h-3" /> QR-Codes der Briefe auf Vorrats-Flyer drucken & abbuchen ({openQr} offen)…
        </button>
        <button onClick={adjust} disabled={busy === 'adjust'} data-testid="flyer-stock-adjust-btn"
          className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50">
          <SlidersHorizontal className="w-3 h-3" /> Bestand korrigieren
        </button>
        {extra}
      </div>
    </div>
  );
}
